import * as Sentry from "@sentry/react";
import { ApolloLink, ApolloClient, ServerError } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";
import { EnvNames } from "Shared/env/envNames";
import handleServerError, { SupportedErrorCodes } from "./apolloClientErrorHandler";
import { apolloClientCache, outdatedCacheLink } from "./apolloClientCache";
import LocalStorageKeys from "Shared/Constants/LocalStorageKeys";
import queryString from "query-string";
import { networkStatusLink } from "./ApolloNetworkStatus";

const createOnErrorLink = (env: EnvNames) =>
  onError(({ networkError }) => {
    const serverError = networkError as ServerError;
    if (SupportedErrorCodes.includes(serverError.statusCode?.toString())) {
      handleServerError(serverError, env);
    } else {
      Sentry.captureException(networkError);
    }
  });

const createApolloClient = (graphQLEndpoint: string, env: EnvNames) => {
  const uploadLink = createUploadLink({
    uri: graphQLEndpoint,
    credentials: "include",
    headers: { crossDomain: true },
  });

  const afterwareLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(response => {
      const context = operation.getContext();
      const {
        response: { headers },
      } = context;

      if (headers) {
        const token = headers.get("X-User-Token");
        token && localStorage.setItem(LocalStorageKeys.userTokenKey, token);
      }
      return response;
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authLink = setContext((_: any, { headers }: any) => {
    const urlFragment = queryString.parse(window.location.hash);
    // fetch tokens from localStorage if they exist.
    const token = localStorage.getItem(LocalStorageKeys.userTokenKey);
    // check url fragment for device token if it is not present.
    const deviceToken = localStorage.getItem(LocalStorageKeys.deviceTokenKey) || urlFragment.device_token;

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        "authorization": token ? `Bearer ${token}` : "",
        "X-Device-Authorization": deviceToken ? `Bearer ${deviceToken}` : "",
      },
    };
  });

  const link = ApolloLink.from([
    networkStatusLink,
    outdatedCacheLink,
    afterwareLink,
    authLink,
    createOnErrorLink(env),
    uploadLink,
  ]);
  return new ApolloClient({
    link,
    cache: apolloClientCache,
    connectToDevTools: true,
  });
};
export default createApolloClient;
